'use strict';
import {Observable} from 'rxjs/Observable';
import 'rxjs/add/operator/mapTo';
import 'rxjs/add/operator/publishReplay';
import 'rxjs/add/operator/startWith';
const angular = require('angular');
const {copy, isArray, isDefined, isFunction, isObject, isString, isUndefined, noop} = angular;

import {DefaultProperties} from './domain-properties/Properties';
import {$$deleteEntityErrorMessage} from './DomainProvider';

//TODO: add support for default filters. Should accommodate plugable user access list filters.
//TODO: add definition function for belongsTo such that following successful save of new items only (use before save to check if $id does not exist), the id is added to the specified link collection of the other object.
//TODO: add $refresh button that fetches the most current data from Firebase.
//TODO: add support for dirty edits, so that we can store the proposed changes to an object and only appl the delta to FB, rather overwriting the whole object, potentially.
export const AbstractDomainClassImport = [
  '$injector',
  '$window',
  '$log',
  '$exceptionHandler',
  '$q',
  '$cacheFactory',
  '$persistentCacheFactory',
  'FirebaseConnectionService',
  'FirebaseObjectFactory',
  '$$FirebaseListener',
  'FirebaseUtil',
  'DomainClassHelper',
  'DomainBootstrapper',
  '$domain',
  '$domainCache',
  function (
    $injector,
    $window,
    $log,
    $exceptionHandler,
    $q,
    $cacheFactory,
    $persistentCacheFactory,
    FirebaseConnectionService,
    FirebaseObjectFactory,
    FirebaseListener,
    FirebaseUtil,
    DomainClassHelper,
    DomainBootstrapper,
    $domain,
    $domainCache,
  ) {

    var cacheUpdateFunctions = {};

    /**
     *
     * @param Class - The concrete Class
     * @param {Object} data - The initial values of this object's persistent properties.
     * @constructor
     */
    function AbstractDomainClass(Class, data) {
      if (!Class) return; // only applicable to instantiations

      Object.defineProperties(this, new DefaultProperties(this, {$class: Class}));

      for (var field in Class.$constraints) {
        if (Class.$constraints.hasOwnProperty(field)) {
          this.$$values.$defineValue(field, Class.$constraints[field]);

          Object.defineProperty(this, field, {
            enumerable: this.$$values[field].isEnumerable(),
            get: this.$$values[field].defineGetter(),
            set: this.$$values[field].defineSetter()
          });

          if (!data) {
            this[field] = this.$getDefaultValue(field);
          }
        }
      }

      if (isObject(data)) {
        this.$$values.$hydrate(data);
      }
    }


    //************************************************************************************************************
    // Static Methods
    //************************************************************************************************************

    AbstractDomainClass.createFactory = function createFactory(Class) {
      Object.defineProperty(Class, '$factory', {value: {}});

      Class.$factory.create = function (data, firebaseListener) {
        var id = data ? data.$id : null;
        var instance;
        if (id) {
          return Class.$factory.instantiate(data);
        } else { // Apply initial values
          instance = new Class();
          id = FirebaseUtil.generateKey();

          instance.$id = id;
          $domainCache.put(Class.COLLECTION, id, instance);
          instance.$$persisted = false;
          instance.$$listener = firebaseListener;

          for (var key in data) {
            if (data.hasOwnProperty(key)) {
              instance[key] = data[key];
            }
          }

          return instance;
        }
      };

      Class.$factory.instantiate = function instantiate(data, firebaseListener) {
        var id = data ? data.$id : null;
        var instance;
        if (!id) {
          id = FirebaseUtil.generateKey();
          instance = new Class(data);
          instance.$id = id;
          instance.$$persisted = false;
          instance.$$listener = firebaseListener;
          $domainCache.put(Class.COLLECTION, instance.$id, instance);
        } else {
          instance = $domainCache.get(Class.COLLECTION, id);
          if (instance) {
            instance.$update(data);
            instance.$$persisted = true;
          } else {
            instance = new Class(data);
            $domainCache.put(Class.COLLECTION, id, instance);
            instance.$$persisted = true;
            instance.$$listener = firebaseListener || _createFirebaseListener(Class.COLLECTION, id);
          }
        }

        return instance;
      };
    };

    //@deprecated. Use $domain.cacheResult
    AbstractDomainClass.cacheResult = $domain.cacheResult;

    // Firebase Methods

    AbstractDomainClass.$get = function $get(id, forceFetch) {
      var deferred = $q.defer();
      var self = this;
      //console.log(this.name + '.$get(' + id + ')');
      var instance = $domainCache.get(this.COLLECTION, id);
      var ref = FirebaseUtil.firebaseRef(this.COLLECTION + '/' + id);

      if (instance) {
        //console.log(this.name + '.$get(' + id + '): instance is in the $domainCache, resolving it.');
        deferred.resolve(instance);
      } else {
        var cache = _getCache.call(this);
        var cachedData = cache.get(id);

        if (cachedData) {
          //console.log(this.name + '.$get(' + id + '): instance is in the persistentCache, resolving it.', cachedData);
          try {
            deferred.resolve(_onReceivedData_$get(cachedData));
            //console.log(this.name + '.$get(' + id + '): instance is in the persistentCache, resolved.');
          } catch (e) {
            $exceptionHandler(e, this.$name + '.$get(' + id + '): instance is in the persistentCache, failed to resolve with error.');
          }
          //TODO: schedule debounced update from firebase
          //_updateCache(this.COLLECTION, id);
        } else {
          if (FirebaseConnectionService.online || forceFetch) {
            //console.log(this.name + '.$get(' + id + '): instance is not in either cache, requesting it from server.');
            FirebaseUtil.read(this.COLLECTION + '/' + id).then(function onReadFirebaseData_$get(dbData) {

              deferred.resolve(_onReceivedData_$get(dbData));

              if (dbData) {
                cache.put(id, dbData);
              } else {
                cache.remove(id);
              }
            }, deferred.reject.bind(deferred));
          } else {
            deferred.reject('AbstractDomainClass.$get Client is offline and retrieval failed');
          }
        }
      }

      function _onReceivedData_$get(data) {
        var id = ref.key();
        //console.log(self.name + '.$get Received ' + id + ' with data ', data);
        if (data === null || isUndefined(data)) {
          return null;
        } else {
          data.$id = id;
          var instance = self.$factory.instantiate(data);

          if (!instance.$ref) {
            instance.$ref = function $ref() {
              return ref;
            };
          }

          return instance;
        }
      }

      return deferred.promise;
    };

    AbstractDomainClass.$list = function $list(params) {
      //var q = $q.defer();

      //var user = AuthService.getUser();

      var config = {
        path: this.COLLECTION,
        factory: this.$factory,
        factoryMethodName: 'create'
      };

      if (params && params.filter) {
        config.filters = [params.filter];
      } else if (params && params.filters) {
        config.filters = params.filters;
      }

      return new FirebaseObjectFactory(config);

      //return q.promise;
    };

    AbstractDomainClass.$remove = function $remove(id) {
      var COLLECTION = this.COLLECTION;
      _getCache.call(this).remove(id);
      var link = {
        id: id,
        collection: COLLECTION
      };

      if (this.hasOwnProperty("$$deleteEntity")) {
        try {
          return $q.when($injector.invoke(this.$$deleteEntity, {}, {$link: link}));
        } catch (error) {
          console.warn($$deleteEntityErrorMessage);
          return $q.reject($$deleteEntityErrorMessage);
        }
      } else {
        return $domain.$$deleteEntity(link);
      }
    };

    function _createFirebaseListener(collection, id) {
      var ref = FirebaseUtil.firebaseRef(collection);
      var cachePath = ref.toString();
      var cache = $persistentCacheFactory.get(cachePath) || $persistentCacheFactory(cachePath, {capacity: 25000});
      return new FirebaseListener(ref.child(id), 'value', updateListener, noop, null, {
        id: id,
        start: true,
        bindOnce: false
      });

      function updateListener(ss) {
        var data = ss.val();
        var instance = $domainCache.get(collection, id);

        if (instance && ss.exists()) {
          instance.$update(data);
          instance.$$persisted = true;
          cache.put(id, data);
        } else if (instance && instance.$$persisted) {
          instance.$destroy();
          cache.remove(id);
        } else if (data) {
          cache.put(id, data);
        }
      }
    }

    function _getCache() {
      var ref = FirebaseUtil.firebaseRef(this.COLLECTION);
      var cachePath = ref.toString();
      return $persistentCacheFactory.get(cachePath) || $persistentCacheFactory(cachePath, {capacity: 25000});
    }


    //************************************************************************************************************
    // Prototype Methods
    //************************************************************************************************************

    AbstractDomainClass.prototype.constructor = AbstractDomainClass;


    //************************************************************************************************************
    //        Primary Methods
    //************************************************************************************************************

    AbstractDomainClass.prototype.$loaded = function $loaded() {
      return this.$$values.$loaded();
    };

    AbstractDomainClass.prototype.$save = function $save(argsMap) {
      if (!this.$isDirty()) {
        console.warn('DomainClass: Attempting to save instance that is not changed', this);
        return $q.when(null);
      } else {
        var self = this;
        argsMap = argsMap || {};
        return this.$loaded().then(function () {
          var deferred = $q.defer();

          self.$$values.$updateAccountingValues();

          var unbound = self.$unbind();

          DomainClassHelper.beforeValidation(self, argsMap).then(function () {
            self.$validate();

            if (self.$hasErrors()) {
              deferred.reject(self.$errors);
            } else {

              var promise = DomainClassHelper.beforeSave(self, argsMap).then(function () {
                var serializedData = self.$serialize();
                return FirebaseUtil.update(self.COLLECTION + '/' + self.$id, serializedData)
                  .then(function (data) {
                    self.$$persisted = true;
                    self.$$values.$write();
                  }, function (error) {
                    deferred.reject(error);
                  });
              }).then(function () {
                return DomainClassHelper.afterSave(self, argsMap);
              }).then(deferred.resolve.bind(deferred, null), deferred.reject.bind(deferred)
              );

              if (unbound || self.$class.$$autoBind) {
                promise.finally(self.$bind.bind(self));
              }
            }
          });

          return deferred.promise;
        });
      }
    };

    AbstractDomainClass.prototype.$serialize = function $serialize(includeAll) {
      return this.$$values.$serialize(includeAll);
    };

    AbstractDomainClass.prototype.$delete = function $delete(argsMap) {
      if (this.$isPersisted()) {
        return this.$class.$remove(this.$id);
      } else {
        return $q.when(null);
      }
      //var self = this;
      //var instanceId = self.$id;
      //var collection = this.$class.COLLECTION;
      //argsMap = argsMap || {};
      //return DomainClassHelper.beforeDelete(self, argsMap).then(function () {
      //  return FirebaseUtil.remove(collection + '/' + self.$id).then(function () {
      //    $domainCache.remove(collection, instanceId);
      //    return DomainClassHelper.afterDelete(self, argsMap);
      //  });
      //});
    };

    AbstractDomainClass.prototype.$isBound = function $isBound() {
      return this.$$listener && this.$$listener.isActive();
    };

    AbstractDomainClass.prototype.$bind = function $bind() {
      if (!this.$$listener) {
        this.$$listener = _createFirebaseListener(this.$class.COLLECTION, this.$id);
      } else {
        this.$$listener.resume();
      }
    };

    AbstractDomainClass.prototype.$unbind = function $unbind() {
      if (this.$$listener) {
        this.$$listener.pause();
        return true;
      } else {
        return false;
      }
    };

    AbstractDomainClass.prototype.$destroy = function $destroy() {
      $domainCache.remove(this.$class.COLLECTION, this.$id);
      // TODO(design): Updating the instance with null may or may not be a good idea. Need to validate this doesn't break things.
      this.$$values.$update(null);

      if (this.$$listener && this.$$listener.destroy) {
        //console.log('AbstractDomainClass.prototype.$destroy FirebaseListener', this.$class.COLLECTION, this.$id);
        this.$$listener.destroy();
      }
    };

    AbstractDomainClass.prototype.$isPersisted = function $isPersisted() {
      return this.$$persisted;
    };

    AbstractDomainClass.prototype.toString = function toString() {
      return this.$class.$name + ':' + this.$id;
    };

    // Validation

    AbstractDomainClass.prototype.$validate = function $validate() {
      this.$errors.$reset();
      DomainClassHelper.beforeValidation(this);  // Warning, this only really works for synchronous before methods.
      for (var field in this.$constraints) {
        if (this.$constraints.hasOwnProperty(field) && this.$$values[field].isEnumerable()) {
          DomainClassHelper.validateConstraint(this, field);
        }
      }
      return this.$errors.$size() === 0;
    };

    AbstractDomainClass.prototype.$hasErrors = function $hasErrors() {
      return this.$errors && this.$errors.__size > 0;
    };

    // Values

    AbstractDomainClass.prototype.$isDirty = function () {
      return this.$$values.$isDirty();
    };

    AbstractDomainClass.prototype.$getDefaultValue = function (propertyName) {
      if (!propertyName || !this.$constraints[propertyName]) {
        return null;
      } else {
        if (isDefined(this.$constraints[propertyName].defaultValue)) {
          if (isFunction(this.$constraints[propertyName].defaultValue)) {
            return this.$constraints[propertyName].defaultValue(this);
          } else if (isObject(this.$constraints[propertyName].defaultValue)) {
            return copy(this.$constraints[propertyName].defaultValue);
          } else {
            return this.$constraints[propertyName].defaultValue;
          }
        }
      }
    };

    AbstractDomainClass.prototype.$update = function (data) {
      this.$$values.$update(data);

      this.$$notify('update', this);
    };

    /**
     * Subscribe to the specified event name on the current AbstractDomainClass.
     *
     * update params: newValue
     *   Called when the instance is updated through the $update method, as in, from firebase
     *
     * @param event
     * @param callback
     * @param context
     * @returns {*}
     */
    AbstractDomainClass.prototype.$on = function $on(event, callback, context) {
      if (event !== 'update') throw new Error(this.$class.$name + '.$on called with invalid event type ' + event);
      if (isUndefined(context)) context = null;
      this.$$listeners[event].push({callback: callback, context: context});
      return callback;
    };

    AbstractDomainClass.prototype.$off = function $off(event, callback) {
      if (event !== 'update') throw new Error(this.$class.$name + '.$off called with invalid event type ' + event);
      if (isUndefined(callback)) {
        while (this.$$listeners[event].length > 0) {
          this.$$listeners[event].pop();
        }
      } else {
        for (var i = 0, length = this.$$listeners[event].length; i < length; i++) {
          if (this.$$listeners[event][i] === callback) {
            this.$$listeners[event].splice(i, 1);
            i--;
          }
        }
      }
    };

    // publish may not work correctly here since we are not memoizing the ref we're observing
    // TODO(AngularDomain): if this happens to see much use, investigate and fix as needed
    AbstractDomainClass.observe = function observe(id) {
      return FirebaseUtil.firebaseRef(this.COLLECTION + '/' + id)
        .observe('value')
        .map(parseInstanceSnapshot);
    };

    function parseInstanceSnapshot(ss) {
      if (!ss.exists()) {
        return null;
      } else {
        const val = ss.val();
        val.id = ss.key();
        val.collection = ss.ref().parent().key();
        return val;
      }
    }

    AbstractDomainClass.prototype.$observe = function (eventType = 'update') {
      if (this.__$$observe) {
        return this.__$$observe;
      }
      this.__$$observe = Observable.create(observer => {
        const listener = this.$on(eventType, val => observer.next(val), error => observer.error(error));
        return () => {
          this.__$$observe = null;
          this.$off(eventType, listener);
        }
      }).publishReplay(1).refCount()
        .startWith(this)
        .mapTo(this);

      return this.__$$observe;
    };

    AbstractDomainClass.prototype.$$notify = function notify(event) {
      if (!this.$$listeners) return;
      var argumentsLength = arguments.length;
      var slicedArguments = new Array(argumentsLength - 1);
      for (var j = 1; j < argumentsLength; j++) {
        slicedArguments = arguments[j];
      }

      for (var i = 0, length = this.$$listeners[event].length; i < length; i++) {
        this.$$listeners[event][i].callback.apply(this.$$listeners[event][i].context, slicedArguments);
      }
    };

    AbstractDomainClass.prototype.$revert = function (propertyName) {
      this.$$values.$revert(propertyName);
    };

    AbstractDomainClass.prototype.$getOriginalValue = function (propertyName) {
      return this.$$values[propertyName].originalValue;
    };

    AbstractDomainClass.prototype.$getLink = function (linkedPropertyName) {
      if (!isObject(this.$constraints[linkedPropertyName])) {
        throw new Error(this.$class.$name + ': Attempting to get a domain-link for an undefined property: ' + linkedPropertyName);
      }
      if (this.$constraints[linkedPropertyName].type !== 'Domain' && this.$constraints[linkedPropertyName].type !== 'DomainLink') {
        throw new Error(this.$class.$name + ': Attempting to get a domain-link for a non-link property: ' + linkedPropertyName);
      }

      var constraint = this.$constraints[linkedPropertyName];
      var result;
      if (this.$$values[linkedPropertyName].isDirty()) {
        if (constraint.domainClass && constraint.simpleLink) {
          result = {id: this.$$values[linkedPropertyName].value, collection: constraint.domainClass.COLLECTION};
        } else if (isObject(this.$$values[linkedPropertyName].value)) {
          result = {
            id: this.$$values[linkedPropertyName].value.id,
            collection: this.$$values[linkedPropertyName].value.collection
          };
        } else {
          result = {id: null, collection: null};
        }
      } else {
        if (constraint.domainClass && constraint.simpleLink) {
          result = {
            id: this.$$values[linkedPropertyName].originalValue,
            collection: constraint.domainClass.COLLECTION
          };
        } else if (isObject(this.$$values[linkedPropertyName].originalValue)) {
          result = {
            id: this.$$values[linkedPropertyName].originalValue.id,
            collection: this.$$values[linkedPropertyName].originalValue.collection
          };
        } else {
          result = {id: null, collection: null};
        }
      }
      return result;
    };

    AbstractDomainClass.prototype.$setLink = function (linkedPropertyName, linkObject) {
      if (!isObject(this.$constraints[linkedPropertyName])) {
        throw new Error(this.$class.$name + ': Attempting to set a domain-link for an undefined property: ' + linkedPropertyName);
      }
      if (this.$constraints[linkedPropertyName].type !== 'Domain' && this.$constraints[linkedPropertyName].type !== 'DomainLink') {
        throw new Error(this.$class.$name + ': Attempting to set a domain-link for a non-link property: ' + linkedPropertyName);
      }

      var constraint = this.$constraints[linkedPropertyName];
      var domainValue = this.$$values[linkedPropertyName];
      if (isObject(linkObject)) {
        if ((linkObject.id && isString(linkObject.id)) && (linkObject.collection && isString(linkObject.collection))) {
          if (constraint.domainClass && constraint.simpleLink) {
            domainValue.value = linkObject.id;
            domainValue.$$dirty = domainValue.value !== domainValue.originalValue;
          } else {
            domainValue.value = {
              id: linkObject.id,
              collection: linkObject.collection
            };
            domainValue.$$dirty = domainValue.domainLinkIsDirty();
          }
          //console.log('$setLink result:', domainValue);
        }
      }
    };

    /**
     * If the domain instance is cached, it returns $q.when(domainInstance).
     * Otherwise it fetches the instance from firebase.
     * @param linkedPropertyName
     * @returns {Promise} A promise that resolves to the domain instance, if it exists.
     */
    AbstractDomainClass.prototype.$getDomainLink = function (linkedPropertyName) {
      if (!isObject(this.$constraints[linkedPropertyName])) {
        throw new Error(this.$class.$name + ': Attempting to get a domain-link for an undefined property: ' + linkedPropertyName);
      }
      var constraint = this.$constraints[linkedPropertyName];
      if (constraint.type !== 'Domain' && constraint.type !== 'DomainLink') {
        throw new Error(this.$class.$name + ': Attempting to get a domain-link for a non-link property: ' + linkedPropertyName);
      }

      var result;
      if (this.$$values[linkedPropertyName].isDirty()) {
        if (constraint.domainClass && constraint.simpleLink) {
          if (this.$$values[linkedPropertyName].value) {
            result = {
              id: this.$$values[linkedPropertyName].value,
              collection: constraint.domainClass.COLLECTION
            };
          } else {
            result = null;
          }
        } else if (isObject(this.$$values[linkedPropertyName].value)) {
          result = {
            id: this.$$values[linkedPropertyName].value.id,
            collection: this.$$values[linkedPropertyName].value.collection
          };
        } else {
          result = null;
        }
      } else {
        if (isObject(this.$$values[linkedPropertyName].originalValue)) {
          result = {
            id: this.$$values[linkedPropertyName].originalValue.id,
            collection: this.$$values[linkedPropertyName].originalValue.collection
          };
        } else {
          result = null;
        }
      }

      if (result) {
        var cached = $domainCache.get(result.collection, result.id);
        if (cached) {
          return $q.when(cached);
        } else {
          return $domain.getDomain(result.collection).$get(result.id);
        }
      } else {
        return $q.when(null);
      }
    };

    AbstractDomainClass.prototype.$setDomainLink = function (linkedPropertyName, domainInstance) {
      if (!isObject(this.$constraints[linkedPropertyName])) {
        throw new Error(this.$class.$name + ': Attempting to set a domain-link for an undefined property: ' + linkedPropertyName);
      }
      if (this.$constraints[linkedPropertyName].type !== 'Domain' && this.$constraints[linkedPropertyName].type !== 'DomainLink') {
        throw new Error(this.$class.$name + ': Attempting to set a domain-link for a non-link property: ' + linkedPropertyName);
      }

      this[linkedPropertyName] = domainInstance; // delegate to the setter, which already accounts for setting simple link objects or live domain instances
    };

    //************************************************************************************************************
    // Interceptors
    //************************************************************************************************************

    /**
     * Callback is invoked with the provided context (or null) immediately prior to validation, when saving.
     * Callback is provided the following arguments:
     * instance: the object being saved.
     * save arguments map: the argument to the $save() method.
     *    Current implemented usage: when you save an instance of a linked collection, mapArgs.'list name' = relational data saved to firebase. defaults to true
     * args: an array of arguments passed as the second argument to $doBeforeSave
     *
     * @param callback
     * @param args
     * @param context
     */
    AbstractDomainClass.$doBeforeValidation = DomainClassHelper.$doBeforeValidation;

    /**
     * Callback is invoked with the provided context (or null) immediately prior to saving, after validation has succeeded.
     * Callback is provided the following arguments:
     * instance: the object being saved.
     * save arguments map: the argument to the $save() method.
     *    Current implemented usage: when you save an instance of a linked collection, mapArgs.'list name' = relational data saved to firebase. defaults to true
     * args: an array of arguments passed as the second argument to $doBeforeSave
     *
     * @param callback
     * @param args
     * @param context
     */
    AbstractDomainClass.$doBeforeSave = DomainClassHelper.$doBeforeSave;

    /**
     * Callback is invoked with the provided context (or null) immediately after saving has succeeded.
     * Callback is provided the following arguments:
     * instance: the object being saved.
     * save arguments map: the argument to the $save() method.
     *    Current implemented usage: when you save an instance of a linked collection, mapArgs.'list name' = relational data saved to firebase. defaults to true
     * args: an array of arguments passed as the second argument to $doAfterSave
     *
     * @param callback
     * @param args
     * @param context
     */
    AbstractDomainClass.$doAfterSave = DomainClassHelper.$doAfterSave;

    /**
     * Callback is invoked with the provided context (or null) immediately prior to deletion
     * Callback is provided the following arguments:
     * instance: the object being deleted.
     * delete arguments map: the argument to the $delete() method.
     *
     * @param callback
     * @param args
     * @param context
     */
    AbstractDomainClass.$doBeforeDelete = DomainClassHelper.$doBeforeDelete;

    /**
     * Callback is invoked with the provided context (or null) immediately after deletion has succeeded.
     * Callback is provided the following arguments:
     * instance: the object being deleted.
     * delete arguments map: the argument to the $delete() method.
     *
     * @param callback
     * @param args
     * @param context
     */
    AbstractDomainClass.$doAfterDelete = DomainClassHelper.$doAfterDelete;


    //************************************************************************************************************
    // Builder Methods
    //************************************************************************************************************

    AbstractDomainClass.__inheritFrom = function (Class, className, collection, config) {
      config = isObject(config) ? config : {autoBind: true};
      // Register the domain class
      $domain.registerDomain(collection, Class);

      //console.log('Defining ' + className);
      Object.defineProperty(Class, "COLLECTION", {value: collection});
      Object.defineProperty(Class, "$constraints", {value: new $domain.Constraints(Class)});
      Object.defineProperty(Class, "$name", {value: className});
      Object.defineProperty(Class, "$$beforeValidationClosures", {value: []});
      Object.defineProperty(Class, "$$beforeSaveClosures", {value: []});
      Object.defineProperty(Class, "$$afterSaveClosures", {value: []});
      Object.defineProperty(Class, "$$beforeDeleteClosures", {value: []});
      Object.defineProperty(Class, "$$afterDeleteClosures", {value: []});
      Object.defineProperty(Class, "$$hasMany", {value: []});
      Object.defineProperty(Class, "$$autoBind", {value: config.autoBind});
      if (config.hasOwnProperty('deleteEntity')) {
        if (!isFunction(config.deleteEntity) && !isArray(config.deleteEntity))
          throw new Error('Invalid config for Domain Class:' + className + ', collection:' + collection + ' requires a function or an annotated function (array) that will delete an entity at runtime.');
        Object.defineProperty(Class, "$$deleteEntity", {value: config.deleteEntity});
      }

      Class.prototype = new AbstractDomainClass();
      Class.prototype.constructor = Class;
      Object.defineProperty(Class.prototype, "COLLECTION", {value: collection});
      Object.defineProperty(Class.prototype, "$constraints", {value: Class.$constraints});

      // inherit static methods here.
      Class.$get = AbstractDomainClass.$get;
      Class.$list = AbstractDomainClass.$list;
      Class.$remove = AbstractDomainClass.$remove;
      Class.observe = AbstractDomainClass.observe;
      Class.$doBeforeValidation = AbstractDomainClass.$doBeforeValidation;
      Class.$doBeforeSave = AbstractDomainClass.$doBeforeSave;
      Class.$doAfterSave = AbstractDomainClass.$doAfterSave;
      Class.$doBeforeDelete = AbstractDomainClass.$doBeforeDelete;
      Class.$doAfterDelete = AbstractDomainClass.$doAfterDelete;

      // generate the caching factory
      AbstractDomainClass.createFactory(Class);

      // force the dependency injector to define the class.
      DomainBootstrapper.queue(function (injector) {
        //console.log('Boostrapping ' + className);
        injector.get(className);
      });
    };

    return AbstractDomainClass;
  }];



// WEBPACK FOOTER //
// ./src/js/_libs/angular-domain/AbstractDomainClass.js